import { useCallback } from 'react';

interface Threshold {
  id: string;
  color: string;
  rating: string;
  logic: string;
  value: string | [number, number];
}

export const useFormValidation = (
  isScoring: boolean,
  scoreConfig: Threshold[],
  detectOverlaps: () => Set<string>,
) => {
  // Helper function to determine if threshold type requires two inputs
  const requiresTwoInputs = (type: string) => type === 'btw';

  // Validation function
  const isValid = useCallback(() => {
    if (!isScoring) return true;

    // Check for valid threshold data
    const validData = scoreConfig.every((threshold) => {
      if (!threshold.rating.trim()) return false;

      if (requiresTwoInputs(threshold.logic)) {
        return (
          Array.isArray(threshold.value) &&
          threshold.value.length === 2 &&
          threshold.value[0] !== undefined &&
          threshold.value[1] !== undefined &&
          !isNaN(threshold.value[0]) &&
          !isNaN(threshold.value[1])
        );
      } else {
        return (
          typeof threshold.value === 'string' && threshold.value.trim() !== ''
        );
      }
    });

    // Check for overlaps
    if (scoreConfig.length > 1) {
      const overlaps = detectOverlaps();
      if (overlaps.size > 0) return false;
    }

    return validData;
  }, [isScoring, scoreConfig, detectOverlaps]);

  return {
    isValid,
    requiresTwoInputs,
  };
};
